import React, { useRef, useEffect, memo } from 'react';
import { FixedSizeList } from 'react-window';
import useResizeObserver from 'use-resize-observer';
import ThumbnailItem from './ThumbnailItem';
import { getFileTypeV2 } from './fileTypeUtils';

const ITEM_WIDTH = 90;
const ITEM_HEIGHT = 110;
const STRIP_HEIGHT = ITEM_HEIGHT + 2;

/**
 * Stable render function defined outside the component so its reference
 * never changes between renders. React Window calls this with { index, style, data }
 * where data = itemData prop passed to FixedSizeList.
 *
 * This prevents FixedSizeList from re-rendering all visible rows whenever
 * selectedIndex or onSelect changes — only the rows whose own props differ
 * will re-render (gated by ThumbnailItem's memo).
 */
const Row = memo(({ index, style, data }) => {
  const { items, selectedIndex, onSelect, versionFileUpload } = data;
  const item = items[index];
  const type = getFileTypeV2(item?.display_name || item?.file_name);
  const itemKey = versionFileUpload ? item?.version_id : item?.object_id;

  return (
    <div
      key={itemKey || index}
      style={style}
      className="thumbnail-strip__item-wrapper"
      onClick={() => onSelect(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(index);
      }}
      aria-label={`Preview ${item?.display_name || item?.file_name}`}
      aria-pressed={index === selectedIndex}
    >
      <ThumbnailItem
        type={type}
        url={item?.url}
        file_path={item?.file_path}
        display_name={item?.display_name || item?.file_name}
        isSelected={index === selectedIndex}
      />
    </div>
  );
});

Row.displayName = 'ThumbnailRow';

/**
 * Virtualized horizontal thumbnail strip.
 * Uses react-window FixedSizeList (horizontal layout) so only the
 * visible thumbnails are rendered — critical for large file lists.
 *
 * itemData is passed as a single stable-shaped object so Row never
 * needs to be recreated. FixedSizeList only re-renders rows whose
 * itemData reference changed AND whose own output differs.
 */
const ThumbnailStrip = ({ items, selectedIndex, onSelect, versionFileUpload }) => {
  const listRef = useRef();
  const { ref: containerRef, width: containerWidth = 0 } = useResizeObserver();

  // Auto-scroll the selected thumbnail into view whenever selectedIndex changes
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      listRef.current.scrollToItem(selectedIndex, 'smart');
    }
  }, [selectedIndex]);

  // itemData is a plain object. FixedSizeList passes it directly to Row.
  // React Window does a shallow comparison on itemData by reference — we
  // intentionally let it update so rows can read the latest selectedIndex,
  // but Row itself is memo'd so only rows where isSelected changed re-paint.
  const itemData = { items, selectedIndex, onSelect, versionFileUpload };

  if (!items || items.length === 0) return null;

  return (
    <div ref={containerRef} className="thumbnail-strip" style={{ height: STRIP_HEIGHT }}>
      {containerWidth > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', width: containerWidth }}>
          <FixedSizeList
            ref={listRef}
            layout="horizontal"
            width={Math.min(containerWidth, items.length * ITEM_WIDTH)}
            height={STRIP_HEIGHT}
            itemCount={items.length}
            itemSize={ITEM_WIDTH}
            overscanCount={5}
            itemData={itemData}
            style={{ overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin', scrollbarColor: 'var(--black-500) var(--black-100)' }}
          >
            {Row}
          </FixedSizeList>
        </div>
      )}
    </div>
  );
};

export default memo(ThumbnailStrip);
